import { ChangeEvent, useEffect, useState } from "react";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { UserRole, BlobStorageModel, BlobDocumentType, BlobUploadRequestInput, BlobDocumentFormat } from "../../gql-types.generated";
import AddEditDialog from "./AddEditDialog";
import FileDropZone from '../FileDropZone';
import { maxFileSize } from "../../util/Constants";
import { getBase64 } from "../../util/Common";
import { FileError } from "react-dropzone";

interface DialogProps {
    isOpen: boolean;
    storageFile?: BlobStorageModel | null | undefined;
    viewerRole?: UserRole | undefined;
    onClose: () => void;
    onSave: (requestData: BlobUploadRequestInput) => void;
    error?: Error | undefined;
}

const StandardExchangeFormatFileDialog: React.FC<DialogProps> = props => {
    const { isOpen, storageFile, viewerRole, onClose, onSave, error } = props;
    const id = storageFile?.id;
    const [isFormDirty, setIsFormDirty] = useState(false); // Dirty state of the form.
    const [comment, setComment] = useState<string>('');
    const [blobDocumentId, setBlobDocumentId] = useState<string | undefined>(undefined);
    const [fileName, setFileName] = useState<string>('');
    const [fileContent, setFileContent] = useState<string>('');
    const [submitted, setSubmitted] = useState(false); // Submitted state of the form
    const [fileExtensionError, setFileExtensionError] = useState<string>('');
    
    // Calculate if the file name field should be disabled
    const isFileNameFieldDisabled = submitted || storageFile?.fileName !== undefined;
    
    useEffect(() => {
        if (!isOpen) {
            resetInitialState();
        } else {
            setFromModel();
        }
    }, [isOpen]);

    useEffect(() => {
        if (storageFile) {
            setFromModel();
        } else {
            resetInitialState();
        }
    }, [storageFile]);

    const setFromModel = () => {
        if (storageFile) {
            // if (storageFile.comment) {
            //     setComment(storageFile.comment);
            // }
            if (storageFile.blobStorageReference?.blobDocumentId) {
                setBlobDocumentId(storageFile.blobStorageReference.blobDocumentId);
            }
            if (storageFile.fileName) {
                setFileName(storageFile.fileName);
            }
        }
    };

    const resetInitialState = () => {
        setSubmitted(false);
        setComment('');
        setBlobDocumentId(undefined);
        setFileName('');
        setFileExtensionError('');
        setIsFormDirty(false);
    }

    // Clean filename to only allow alphanumeric, underscore, hyphen, and period
    const sanitizeFileName = (input: string): string => {
        // Only sanitize if the field is enabled
        if (isFileNameFieldDisabled) {
            return input; // Return as-is when disabled
        }
        return input.replace(/[^a-zA-Z0-9._-]/g, '');
    };

    // Validate file extension (case-insensitive)
    const validateFileExtension = (fileName: string): string => {
        if (!fileName.trim()) return '';
        
        const extension = fileName.split('.').pop()?.toLowerCase();
        if (!extension || (extension !== 'xml' && extension !== 'sef')) {
            return 'File extension must be .sef or .xml';
        }
        return '';
    };

    const onEditCancel = () => {
        onClose();
    };


    const onError = () => {
        setSubmitted(false);
    }

    const isFormValid = () => {
        let requiredCheck = fileName?.trim().length > 0 && fileContent?.trim().length > 0 && comment?.trim().length > 0;
        
        // Validate extension and update error state
        const extensionError = validateFileExtension(fileName);
        setFileExtensionError(extensionError);
        let extensionValid = extensionError === '';
        
        return isFormDirty && requiredCheck && extensionValid;
    }

    const submitForm = () => {
        setSubmitted(true);
        onSave({
            comment: comment,
            blobDocumentId: blobDocumentId,
            id: id,
            isActive: true,
            isFirstVersion: blobDocumentId ? false : true,
            fileContent: fileContent,
            fileNameWithExtension: fileName,
            blobDocumentType: BlobDocumentType.Sef,
            blobDocumentFormat: BlobDocumentFormat.Base64,
        } as BlobUploadRequestInput);
    }

    const onFileNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        const sanitizedValue = sanitizeFileName(event.target.value);
        setFileName(sanitizedValue);
    };

    const onCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setComment(event.target.value);
    };

    const onFileNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Only prevent invalid characters if field is enabled
        if (!isFileNameFieldDisabled) {
            const char = event.key;
            // Allow navigation keys, backspace, delete, etc.
            if (char.length === 1 && !/[a-zA-Z0-9._-]/.test(char)) {
                event.preventDefault();
            }
        }
    };

    const getFileContent = async (file: File) => {

        let fileContentBase64 = "";

        // convert file to base64 string to pass to server
        await getBase64(file).then(result => {
            let resultParts = result?.split(',');
            if (resultParts) {
                fileContentBase64 = resultParts[1] ?? '';
            }
        });

        return fileContentBase64;
    };

    const onFileUploadChange = async (filesToUpload: File[]) => {
        setIsFormDirty(true);
        if (filesToUpload && filesToUpload.length > 0) {
            // only 1 file allowed, so just get contents of first file
            let fileToUpload = filesToUpload[0];
            
            let fileString = await getFileContent(fileToUpload);
            setFileContent(fileString);

            // if not a version file, set the file name to the file name
            if (storageFile?.fileName === undefined) {
                let fileName = fileToUpload?.name;
                // Always sanitize uploaded file names since this happens when field is enabled
                const sanitizedFileName = sanitizeFileName(fileName);
                setFileName(sanitizedFileName);
            }
        } else {
            // when no file, make sure any previous file fields are cleared            
            setFileContent('');
            if (storageFile?.fileName === undefined) {
                setFileName('');
                setFileExtensionError('');
            }
        }
    };

    // Custom validator function to restrict file types to .sef and .xml only in the FileDropZone
    const dropZoneExtensionValidator = (file: File): FileError[] | null => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (fileExtension !== 'sef' && fileExtension !== 'xml') {
            return [{ code: 'invalid-extension', message: 'Only .sef and .xml files are allowed.' }] as FileError[]; // Type assertion for custom error
        }
        return null;
    };

    const fileNameProps = {
        'aria-label': 'file name',
        'maxLength': 255,
    };

    const commentProps = {
        'aria-label': 'comment',
        'maxLength': 255,
    };

    return (
        <AddEditDialog
            isOpen={isOpen}
            isSubmitted={submitted}
            id={id}
            titlePrefix="Upload New"
            entityName={`SEF File ${storageFile ? 'Version' : ''} `}
            maxWidth='sm'
            onClose={onClose}
            onSave={submitForm}
            validate={isFormValid}
            onError={onError}
            error={error}
            onCancel={onEditCancel}
            viewerRole={viewerRole}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} >
                    <FileDropZone
                        label="SEF File *"
                        maxFileSize={maxFileSize}
                        maxNumberFiles={1}
                        dropInstructionText="Drop your SEF file here"
                        acceptedFileTypes={{
                            'text/plain': ['.sef'],
                            'text/xml': ['.xml']
                        }}
                        customValidator={dropZoneExtensionValidator}
                        onChange={onFileUploadChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-sef-file-name"
                        fullWidth
                        disabled={isFileNameFieldDisabled}
                        value={fileName}
                        label="File Name"
                        inputProps={fileNameProps}
                        onChange={onFileNameChange}
                        onKeyDown={onFileNameKeyDown}
                        required
                        autoComplete="off"
                        data-cy="dialog-sef-file-name"
                        variant="standard"
                        error={!!fileExtensionError}
                        helperText={fileExtensionError}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-sef-file-comment"
                        fullWidth
                        disabled={submitted}
                        value={comment}
                        label="Comment"
                        inputProps={commentProps}
                        onChange={onCommentChange}
                        required
                        autoComplete="off"
                        data-cy="dialog-sef-file-description"
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel 
                        control={
                            <Checkbox 
                                checked={true}
                                disabled={true}
                            />
                        } 
                        label="Active Version" 
                    />
                </Grid>
            </Grid>
        </AddEditDialog>
    )
}

export default StandardExchangeFormatFileDialog;
